/**
 * The forward-curve generator.
 *
 * One curve per (hub, assessment day): 72 delivery months, each with an
 * on-peak / off-peak / ATC price. Everything else in the module — every strip
 * mode, every summary statistic, the Overview cards — is derived from this, so
 * the numbers agree with each other by construction.
 *
 * Shape of the model, front to back:
 *   level  = base × exp(national + ISO + hub noise)      moves most
 *   anchor = base × exp(half the national + long-run)    moves least
 *   month  = blend(level, anchor) × seasonal × term slope × small noise
 *
 * That blend is what makes a 12-month strip more volatile than a 60-month one
 * while keeping them correlated: they share the national factor.
 *
 * Every price here is invented. It is shaped to be *plausible* — realistic
 * levels, the CAISO solar duck, ERCOT summer scarcity, winter gas basis — so
 * the UI can be judged, not so anyone can trade on it.
 */

import { periodOfDay, addMonths } from "../calendar";
import { round3, blendAtc, type CurveMonth } from "../strips";
import { hubByCode } from "../topology";
import { onPeakFraction } from "../calendar";
import { fbm, gaussAt, seedOf, type Octave } from "./noise";

/** The anchor "today" for the mock. A Friday, so it is a business day. */
export const MOCK_TODAY_ISO = "2026-09-04";
/** Whole days since epoch for {@link MOCK_TODAY_ISO}. */
export const MOCK_TODAY_DAY = 20700;
/** No generated history before this. A longer request truncates. */
export const HISTORY_START_DAY = MOCK_TODAY_DAY - 1460;

const NATIONAL_POWER: Octave[] = [[365, 0.18], [91, 0.08], [30, 0.04], [7, 0.02]];
const NATIONAL_GAS: Octave[] = [[365, 0.28], [91, 0.12], [30, 0.06], [7, 0.03]];
const ISO_FACTOR: Octave[] = [[365, 0.08], [91, 0.05], [30, 0.03]];
const HUB_IDIOSYNCRATIC: Octave[] = [[91, 0.03], [30, 0.02]];
const LONG_RUN: Octave[] = [[730, 0.08], [182, 0.04]];

/** Seasonal multiplier by delivery month, Jan first. Mean is about 1. */
const SEASON: Record<string, readonly number[]> = {
  pjm: [1.12, 1.05, 0.9, 0.85, 0.88, 1.02, 1.18, 1.15, 0.95, 0.88, 0.95, 1.07],
  ercot: [1.0, 0.98, 0.8, 0.78, 0.9, 1.1, 1.35, 1.45, 1.05, 0.82, 0.85, 0.92],
  caiso: [1.05, 0.95, 0.8, 0.72, 0.75, 0.9, 1.2, 1.35, 1.3, 1.05, 0.95, 0.98],
  northeast: [1.45, 1.35, 1.0, 0.8, 0.75, 0.85, 1.0, 0.98, 0.8, 0.78, 0.92, 1.32],
  central: [1.1, 1.02, 0.88, 0.85, 0.9, 1.05, 1.2, 1.15, 0.95, 0.88, 0.95, 1.07],
  gas: [1.2, 1.15, 1.0, 0.9, 0.88, 0.9, 0.93, 0.93, 0.92, 0.95, 1.05, 1.19],
};

/**
 * On-peak / off-peak ratio by delivery month.
 *
 * CAISO drops BELOW 1 in spring: midday solar pushes on-peak under off-peak.
 * That is the duck curve, and it is the detail that tells an energy reader
 * whether the model is real.
 */
const RATIO: Record<string, readonly number[]> = {
  pjm: [1.25, 1.25, 1.28, 1.28, 1.28, 1.4, 1.4, 1.4, 1.4, 1.28, 1.28, 1.25],
  ercot: [1.2, 1.2, 1.25, 1.25, 1.25, 1.65, 1.65, 1.9, 1.65, 1.25, 1.25, 1.2],
  caiso: [1.1, 1.1, 0.88, 0.88, 0.88, 1.0, 1.25, 1.25, 1.25, 1.05, 1.05, 1.1],
  northeast: [1.3, 1.3, 1.22, 1.22, 1.22, 1.35, 1.35, 1.35, 1.35, 1.22, 1.22, 1.3],
  central: [1.2, 1.2, 1.25, 1.25, 1.25, 1.3, 1.3, 1.3, 1.3, 1.25, 1.25, 1.2],
};

interface HubParams {
  /** 2026 level: $/MWh for power, $/MMBtu for gas. */
  base: number;
  season: keyof typeof SEASON;
  ratio: keyof typeof RATIO;
  /** Term structure: positive is contango, negative backwardation. */
  slopePerYear: number;
  /** Delivery months published. SPP North stops early, on purpose. */
  horizonMonths: number;
  /** Gas only: extra winter basis over Henry Hub. */
  winterBasis?: number;
  /** Gas only: floor, so Waha can print negative but not absurdly. */
  floor?: number;
}

const PARAMS: Record<string, HubParams> = {
  pjm_west: p(52, "pjm", "pjm", 0.02),
  pjm_comed: p(40, "pjm", "pjm", 0.02),
  pjm_atsi: p(46, "pjm", "pjm", 0.02),
  pjm_bge: p(58, "pjm", "pjm", 0.02),
  pjm_pseg: p(56, "pjm", "pjm", 0.02),
  pjm_dom: p(54, "pjm", "pjm", 0.02),
  caiso_sp15: p(47, "caiso", "caiso", 0.01),
  caiso_np15: p(45, "caiso", "caiso", 0.01),
  caiso_dlap_sce: p(49, "caiso", "caiso", 0.01),
  ercot_hb_north: p(50, "ercot", "ercot", -0.03),
  ercot_hb_houston: p(53, "ercot", "ercot", -0.03),
  ercot_hb_west: p(44, "ercot", "ercot", -0.03),
  nyiso_g: p(58, "northeast", "northeast", 0.015),
  nyiso_j: p(75, "northeast", "northeast", 0.015),
  nyiso_a: p(38, "northeast", "northeast", 0.015),
  isone_mass_hub: p(66, "northeast", "northeast", 0.015),
  isone_nema: p(67, "northeast", "northeast", 0.015),
  isone_ct: p(67, "northeast", "northeast", 0.015),
  miso_hub_indiana: p(40, "central", "central", 0),
  miso_hub_illinois: p(38, "central", "central", 0),
  miso_hub_michigan: p(42, "central", "central", 0),
  spp_south: p(35, "central", "central", 0),
  // Stops at 48 months, so the 60-month strip is honestly absent here.
  spp_north: { ...p(32, "central", "central", 0), horizonMonths: 48 },

  henry_hub: gasParams(3.6, 0.04),
  eastern_gas_north: gasParams(2.85, 0.02),
  algonquin_cg: gasParams(5.1, 0.02, 2.6),
  iroquois_z2: gasParams(4.9, 0.02, 2.2),
  tenn_z6: gasParams(5.0, 0.02, 2.4),
  transco_z6_ny: gasParams(4.6, 0.02, 2.0),
  transco_z6_nny: gasParams(4.3, 0.02, 1.6),
  transco_z5: gasParams(4.0, 0.02, 1.0),
  tetco_m3: gasParams(4.2, 0.02, 1.4),
  chicago_cg: gasParams(3.75, 0.03),
  pge_cg: gasParams(4.4, 0.03, 0.8),
  socal_cg: gasParams(5.1, 0.03, 1.6),
  socal_gas: gasParams(4.2, 0.03, 0.6),
};

function p(
  base: number,
  season: keyof typeof SEASON,
  ratio: keyof typeof RATIO,
  slopePerYear: number,
): HubParams {
  return { base, season, ratio, slopePerYear, horizonMonths: 72 };
}

function gasParams(base: number, slopePerYear: number, winterBasis?: number): HubParams {
  return {
    base, season: "gas", ratio: "central", slopePerYear,
    horizonMonths: 72, winterBasis, floor: 0.2,
  };
}

/** How far the front of the curve blends into the long-run anchor, in months. */
const BLEND_TAU = 18;

/**
 * The forward curve for one hub on one assessment day.
 *
 * Gas returns ATC only — `onPeak` and `offPeak` are `null`, exactly as the
 * wire sends them, because a gas curve has no peak split.
 */
export function generateCurve(hubCode: string, assessDay: number): CurveMonth[] {
  const hub = hubByCode(hubCode);
  const params = PARAMS[hubCode];
  if (!hub || !params) return [];

  const isGas = hub.commodity === "gas";
  const nationalSeed = seedOf(isGas ? "national|gas" : "national|power");
  const nationalOctaves = isGas ? NATIONAL_GAS : NATIONAL_POWER;
  // Recentred on the anchor date. Without this the seed alone decides the
  // headline level: the gas factor happens to sit near its floor on
  // 2026-09-04, which knocked ~40% off every gas price on the one date the
  // whole module displays. Subtracting the anchor value keeps the SHAPE of the
  // series (it still wanders) while pinning today near `base`.
  const national =
    fbm(nationalSeed, assessDay, nationalOctaves) -
    fbm(nationalSeed, MOCK_TODAY_DAY, nationalOctaves);
  // Same recentring for every factor, so `base` is what a hub actually prints
  // on the anchor date and the parameter table stays readable.
  const isoSeed = seedOf(`iso|${hub.iso}`);
  const hubSeed = seedOf(`hub|${hubCode}`);
  const longRunSeed = seedOf(`longrun|${hubCode}`);
  const regional =
    fbm(isoSeed, assessDay, ISO_FACTOR) - fbm(isoSeed, MOCK_TODAY_DAY, ISO_FACTOR);
  const idiosyncratic =
    fbm(hubSeed, assessDay, HUB_IDIOSYNCRATIC) -
    fbm(hubSeed, MOCK_TODAY_DAY, HUB_IDIOSYNCRATIC) +
    0.006 * gaussAt(seedOf(`daily|${hubCode}`), assessDay);
  const longRun =
    fbm(longRunSeed, assessDay, LONG_RUN) - fbm(longRunSeed, MOCK_TODAY_DAY, LONG_RUN);

  const level = params.base * Math.exp(national + 0.7 * regional + idiosyncratic);
  const anchor = params.base * Math.exp(0.5 * national + longRun);

  const seasonal = SEASON[params.season];
  const ratios = RATIO[params.ratio];
  const fraction = onPeakFraction(hub.iso);
  const epsilonSeed = seedOf(`epsilon|${hubCode}`);
  const startPeriod = periodOfDay(assessDay);

  const out: CurveMonth[] = [];
  for (let j = 1; j <= params.horizonMonths; j += 1) {
    const period = addMonths(startPeriod, j);
    const monthIndex = (period % 100) - 1;
    const weight = Math.exp(-(j - 1) / BLEND_TAU);

    let value =
      (weight * level + (1 - weight) * anchor) *
      seasonal[monthIndex] *
      (1 + (params.slopePerYear * Math.min(j, 60)) / 12) *
      (1 + 0.004 * gaussAt(epsilonSeed, assessDay * 97 + j));

    if (isGas) {
      if (params.winterBasis) {
        value += params.winterBasis * (seasonal[monthIndex] - 1) * 1.6;
      }
      const floored = Math.max(value, params.floor ?? 0.2);
      out.push({ period, onPeak: null, offPeak: null, atc: round3(floored) });
      continue;
    }

    const ratio = ratios[monthIndex] * (1 + 0.02 * gaussAt(epsilonSeed, assessDay * 89 + j));
    const offPeak = round3(value / (fraction * ratio + 1 - fraction));
    const onPeak = round3(offPeak * ratio);
    out.push({ period, onPeak, offPeak, atc: blendAtc(onPeak, offPeak, fraction) });
  }
  return out;
}

/** REC assessment for a certificate on a day. Slow-moving, with steps. */
export function generateRecPrice(certificateId: string, day: number): number {
  const bases: Record<string, number> = {
    RECPAT1: 34, RECNJT1: 22, RECMDT1: 28, RECOHI0: 6.5,
    RECCTC1: 36, RECMAC1: 38, RECCAB1: 12,
  };
  const base = bases[certificateId] ?? 20;
  const seed = seedOf(`rec|${certificateId}`);
  const slow =
    0.15 * valueNoiseSafe(seed, day, 365) + 0.06 * valueNoiseSafe(seed + 31, day, 91);
  const step = 0.05 * gaussAt(seed + 77, Math.floor(day / 10));
  // Quantised to 5c — certificates trade in coarse increments.
  return Math.round(base * Math.exp(slow + step) * 20) / 20;
}

function valueNoiseSafe(seed: number, x: number, period: number): number {
  return fbm(seed, x, [[period, 1]]);
}
